import './ScheduleDayInfoModal.scss';
import './ScheduleUser/Style.scss';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Button, Form, InputGroup, Modal, Row } from 'react-bootstrap';
import { BaseModalInterface } from '../../Interfaces/ModalInterface';
import { getLocalization } from '../../global/global';
import { ScheduleInterface } from '../../Interfaces/ScheduleInterface';
import { User } from '../../Interfaces/User';
import { pad } from '../../utils/utils';
import { ScheduleSelectUser } from '../Schedule/ScheduleSelectUser';
import RepeatOn from './ScheduleUser/RepeatOn';

export interface DayScheduleInterface extends Omit<ScheduleInterface, 'formId' | 'initialized'> {
  title: string;
}

export interface IScheduleUserSelectionModalProps extends BaseModalInterface {
  selectedDate: number;
  addNewSchedule: (user: User, schedule?: Partial<ScheduleInterface>) => void;
}

const className = 'ScheduleUserSelectionModal';

export const ScheduleUserSelectionModal: FunctionComponent<IScheduleUserSelectionModalProps> = (props) => {

  const [schedule, setSchedule] =
    useState<Pick<
    ScheduleInterface, 'repeat' | 'frequency' | 'every' | 'ends' | 'endAfter' | 'endDate' | 'repeatDay'
    >>({});

  const [user, setUser] = useState<User | null>(null);
  const [minDate, setMinDate] = useState('');
  const [validate, setValidate] = useState(false);

  useEffect(() => {
    const d = new Date(props.selectedDate);
    d.setDate(d.getDate() + 1);
    setMinDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
  }, [props.selectedDate]);

  const addSchedule = () => {
    if (
      !user || (schedule.repeat && (
        !schedule.frequency ||
        (schedule.ends === 'DATE' && !schedule.endDate) ||
        (schedule.ends === 'AFTER' && !schedule.endAfter) ||
        !schedule.ends
      ))) {
      setValidate(true);
      return;
    }
    if (user) {
      props.addNewSchedule(user, schedule);
      props.onClose();
    }
  };

  const repeatOnOptions = useMemo(() => {
    return ['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, index) => {
      return (
        <RepeatOn
          key={`repeat-day-${index}`}
          label={d}
          checked={Number(schedule.repeatDay) === index + 1}
          onChange={() => setSchedule({...schedule, repeatDay: index + 1})}
        />
      );
    });
  }, [schedule]);

  return (
    <Modal
      show
      backdrop={'static'}
      className={className}
      size={'sm'}
    >
      <Modal.Header closeButton>
        {getLocalization('addSchedule')}
      </Modal.Header>
      <Modal.Body className={`${className}-body`}>
        <ScheduleSelectUser
          baseClassName={''}
          value={user}
          validate={validate}
          setSelectedUser={(u) => {
            setUser(u);
          }}
        />
        <Form.Group>
          <Form.Check
            name="repeat-schedule-value"
            type="checkbox"
            label={getLocalization('repeat')}
            id="repeat-schedule-value"
            className="repeat-form-check mb-4"
            checked={schedule.repeat}
            onChange={(e) => setSchedule({ repeat: e.target.checked })}
          />
        </Form.Group>
        {schedule.repeat ? (
          <>
            <Form.Group className="repeat-schedule-inline">
              <Form.Label>{getLocalization('every')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                className="repeat-input"
                value={schedule.frequency}
                isInvalid={validate && !schedule.frequency}
                onChange={(e) => setSchedule({...schedule, frequency: Number(e.target.value)})}
              />
              <Form.Control
                as="select"
                onChange={(e) => {
                  const rd = e.target.value === 'WEEK' ? new Date(props.selectedDate).getDay() : undefined;
                  setSchedule({...schedule, every: e.target.value, repeatDay: rd});
                }}
                value={schedule.every}
                size="sm"
              >
                <option value={'DAY'}>{getLocalization('day')}</option>
                <option value={'WEEK'}>{getLocalization('week')}</option>
                <option value={'MONTH'}>{getLocalization('month')}</option>
              </Form.Control>
            </Form.Group>
            {schedule.every === 'WEEK' && (
              <Form.Group>
                <Form.Label>{getLocalization('repeatOn')}</Form.Label>
                <Row>{repeatOnOptions}</Row>
              </Form.Group>
            )}
            <Form.Group>
              <Form.Label>{getLocalization('ends')}</Form.Label>
              <Form.Check
                name="repeat-ends-value"
                type="radio"
                label={getLocalization('never')}
                id="form-check-ends-never"
                className="repeat-form-check mb-4"
                value="NEVER"
                checked={schedule.ends === 'NEVER'}
                isInvalid={validate && !schedule.ends}
                onChange={(e) => setSchedule({...schedule, ends: e.target.checked ? 'NEVER' : schedule.ends})}
              />
              <Form.Group className="repeat-schedule-inline">
                <Form.Check
                  name="repeat-ends-value"
                  type="radio"
                  label={getLocalization('on')}
                  id="form-check-ends-on"
                  className="repeat-form-check"
                  value="DATE"
                  checked={schedule.ends === 'DATE'}
                  isInvalid={validate && !schedule.ends}
                  onChange={(e) => setSchedule({...schedule, ends: e.target.checked ? 'DATE' : schedule.ends})}
                />
                <Form.Control
                  size="sm"
                  type="date"
                  className="repeast-date-input"
                  disabled={schedule.ends !== 'DATE'}
                  value={schedule.endDate}
                  min={minDate}
                  isInvalid={validate && schedule.ends === 'DATE' && !schedule.endDate}
                  onChange={(e) => setSchedule({...schedule, endDate: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="repeat-schedule-inline">
                <Form.Check
                  name="repeat-ends-value"
                  type="radio"
                  label={getLocalization('after')}
                  id="form-check-ends-after"
                  className="repeat-form-check"
                  value="AFTER"
                  checked={schedule.ends === 'AFTER'}
                  isInvalid={validate && !schedule.ends}
                  onChange={(e) => setSchedule({
                    ...schedule, ...(e.target.checked ? { ends: 'AFTER', endDate: undefined } : {})
                  })}
                />
                <InputGroup size="sm" className="repeat-input-group">
                  <Form.Control
                    type="number"
                    disabled={schedule.ends !== 'AFTER'}
                    value={schedule.endAfter}
                    isInvalid={validate && schedule.ends === 'AFTER' && !schedule.endAfter}
                    onChange={(e) => setSchedule({...schedule, endAfter: Number(e.target.value)})}
                  />
                  <InputGroup.Text id="basic-addon2">{getLocalization('occurrences')}</InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </Form.Group>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="secondary" onClick={() => props.onClose()}>
          {getLocalization('cancel')}
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={() => addSchedule()}>
          {getLocalization('addSchedule')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
